import React, { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Minus, 
  Gear, 
  Wallet, 
  ClockCounterClockwise, 
  Target,
  ArrowLeft,
  UserPlus,
  Coins
} from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Button } from './components/ui/button';
import { AppProvider, useApp } from './context/AppContext';
import { useTranslation } from './hooks/useTranslation';
import { CharacterCard } from './components/CharacterCard';
import { CoinDisplay } from './components/CoinDisplay';
import { TransactionModal } from './components/TransactionModal';
import { CharacterModal } from './components/CharacterModal';
import { GoalModal } from './components/GoalModal';
import { HistoryList } from './components/HistoryList';
import { GoalsList } from './components/GoalsList';
import { SettingsSheet } from './components/SettingsSheet';
import { EmptyWalletButton } from './components/EmptyWalletButton';
import { gameSystems, getTotalInBaseUnits, createEmptyWallet, getSymbol } from './data/gameSystems';
import { Toaster, toast } from 'sonner';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { AdBanner } from './components/AdBanner';
import './index.css';

const HomeScreen = ({ onOpenWallet }) => {
  const { state, dispatch } = useApp();
  const { t } = useTranslation();
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  return (
    <div className="min-h-screen bg-[var(--background)] flex flex-col">
      <div className="max-w-md mx-auto flex-1 flex flex-col w-full pb-24">
        
        {/* Header */}
        <header className="px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-0 flex items-center justify-center">
          <div className="flex items-center justify-center gap-0 sm:gap-0.5 w-full max-w-md">
            <img
              src={`${process.env.PUBLIC_URL}/icons/mascot-wallet.png`}
              alt=""
              className="h-11 w-11 sm:h-12 sm:w-12 shrink-0 object-contain select-none"
              aria-hidden
            />
            <img
              src="https://customer-assets.emergentagent.com/job_dungeon-wallet/artifacts/hjfkaubt_logo%20%283%29.png"
              alt="Dungeon Wallet"
              className="h-10 sm:h-11 w-auto max-w-[calc(100%-2.75rem)] sm:max-w-[20rem] object-contain object-left"
            />
          </div>
        </header>
        
        {/* Content */}
        <main className="flex-1 px-5">
          <div className="flex flex-col items-center mb-4 mt-2 gap-2">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCharacterModal(true)}
                className="text-[var(--primary)] h-8 px-2 text-sm"
                data-testid="add-character-btn"
              >
                <UserPlus size={16} className="mr-1" />
                {t('createCharacter')}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(true)}
                className="text-[var(--text-muted)] h-8 w-8"
                data-testid="settings-btn"
              >
                <Gear size={20} />
              </Button>
            </div>
          </div>
          
          {state.characters.length === 0 ? (
            <motion.div 
              className="p-8 rounded-2xl bg-[var(--surface)] text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-[var(--primary)]/10 flex items-center justify-center mx-auto mb-4">
                <UserPlus size={32} weight="thin" className="text-[var(--primary)]" />
              </div>
              <p className="text-[var(--text-muted)] mb-1">{t('noCharacters')}</p>
              <p className="text-sm text-[var(--text-muted)] mb-5">{t('createFirstCharacter')}</p>
              <Button
                onClick={() => setShowCharacterModal(true)}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white rounded-xl px-5 h-10"
                data-testid="create-first-character-btn"
              >
                <Plus size={18} className="mr-2" />
                {t('createCharacter')}
              </Button>
            </motion.div>
          ) : (
            <div className="space-y-2">
              {state.characters.map((char, index) => (
                <CharacterCard
                  key={char.id}
                  character={char}
                  onOpenWallet={onOpenWallet}
                />
              ))}
            </div>
          )}
        </main>
      </div>
      
      <CharacterModal
        isOpen={showCharacterModal}
        onClose={() => setShowCharacterModal(false)}
      />
      
      <SettingsSheet
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
};

// Wallet Screen
const WalletScreen = ({ onBack }) => {
  const { state, dispatch, selectedCharacter } = useApp();
  const { t, lang } = useTranslation();
  
  const [showSettings, setShowSettings] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [transactionType, setTransactionType] = useState('add');
  const [editingGoal, setEditingGoal] = useState(null);
  const [activeTab, setActiveTab] = useState('wallet');
  const [walletViewMode, setWalletViewMode] = useState(() => {
    return localStorage.getItem('walletViewMode') || 'tiles';
  });
  
  const handleViewModeChange = (mode) => {
    setWalletViewMode(mode);
    localStorage.setItem('walletViewMode', mode);
  };
  
  if (!selectedCharacter) return null;
  
  const system = gameSystems[selectedCharacter.systemId];
  const defaultCurrency = system.currencies.find(c => c.id === system.defaultCurrency);
  
  const handleDeleteCharacter = () => {
    dispatch({ type: 'DELETE_CHARACTER', payload: selectedCharacter.id });
    setShowSettings(false);
    onBack();
  };
  
  const handleEmptyWallet = () => {
    dispatch({
      type: 'EMPTY_WALLET',
      payload: { characterId: selectedCharacter.id }
    });
    toast.success(t('emptySuccess'));
  };
  
  const getTotalDisplay = () => {
    const totalBase = getTotalInBaseUnits(selectedCharacter.wallet, selectedCharacter.systemId);
    return (totalBase / defaultCurrency.valueInBase).toFixed(0);
  };
  
  return (
    <div className="min-h-screen bg-[var(--background)]">
      <div className="max-w-md mx-auto min-h-screen flex flex-col">
        
        {/* Header */}
        <header className="px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="text-[var(--text-muted)] -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft size={22} />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[var(--text)]">{selectedCharacter.name}</h1>
            <p className="text-xs text-[var(--text-muted)]">{system.name}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSettings(true)}
            className="text-[var(--text-muted)]"
            data-testid="wallet-settings-btn"
          >
            <Gear size={20} />
          </Button>
        </header>
        
        {/* Total */}
        <div className="px-5 mb-4">
          <div className="p-5 rounded-2xl bg-[var(--surface)] relative">
            <p className="text-sm text-[var(--text-muted)] mb-1">{t('totalValue')}</p>
            <p className="text-4xl font-bold text-[var(--gold)]" data-testid="total-value">
              {getTotalDisplay()}
              <span className="text-base font-medium ml-2 opacity-70">
                {getSymbol(defaultCurrency, lang)}
              </span>
            </p>
            <div className="absolute top-3 right-3">
              <EmptyWalletButton onConfirm={handleEmptyWallet} />
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex-1 px-5 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-[var(--surface)] rounded-xl p-1 mb-4 h-11">
              <TabsTrigger 
                value="wallet" 
                className="flex-1 rounded-lg text-sm data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white"
                data-testid="tab-wallet"
              >
                <Wallet size={16} className="mr-1.5" />
                {t('wallet')}
              </TabsTrigger>
              <TabsTrigger 
                value="history" 
                className="flex-1 rounded-lg text-sm data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white"
                data-testid="tab-history"
              >
                <ClockCounterClockwise size={16} className="mr-1.5" />
                {t('history')}
              </TabsTrigger>
              <TabsTrigger 
                value="goals" 
                className="flex-1 rounded-lg text-sm data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white"
                data-testid="tab-goals"
              >
                <Target size={16} className="mr-1.5" />
                {t('goals')}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="wallet" className="mt-0">
              <CoinDisplay 
                wallet={selectedCharacter.wallet} 
                systemId={selectedCharacter.systemId}
                viewMode={walletViewMode}
                onViewModeChange={handleViewModeChange}
              />
            </TabsContent>
            
            <TabsContent value="history" className="mt-0">
              <HistoryList 
                transactions={selectedCharacter.transactions} 
                systemId={selectedCharacter.systemId}
              />
            </TabsContent>
            
            <TabsContent value="goals" className="mt-0">
              <div className="flex justify-end mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEditingGoal(null); setShowGoalModal(true); }}
                  className="text-[var(--primary)] h-8 px-2 text-sm"
                  data-testid="add-goal-btn"
                >
                  <Plus size={16} className="mr-1" />
                  {t('createGoal')}
                </Button>
              </div>
              <GoalsList 
                goals={selectedCharacter.goals}
                wallet={selectedCharacter.wallet}
                systemId={selectedCharacter.systemId}
                onEdit={(goal) => { setEditingGoal(goal); setShowGoalModal(true); }}
                onDelete={(id) => {
                  dispatch({ type: 'DELETE_GOAL', payload: { characterId: selectedCharacter.id, goalId: id } });
                  toast.success(t('delete'));
                }}
              />
            </TabsContent>
          </Tabs>
        </div>
        
        {/* FAB */}
        <div className="fixed left-0 right-0 flex justify-center gap-3 px-5 z-30 bottom-[calc(6rem+env(safe-area-inset-bottom,0px))]">
          <motion.button
            onClick={() => { setTransactionType('spend'); setShowTransactionModal(true); }}
            className="flex-1 max-w-[150px] h-12 rounded-xl bg-red-500 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-red-500/25"
            whileTap={{ scale: 0.95 }}
            data-testid="spend-btn"
          >
            <Minus size={20} weight="bold" />
            {t('spend')}
          </motion.button>
          
          <motion.button
            onClick={() => { setTransactionType('add'); setShowTransactionModal(true); }}
            className="flex-1 max-w-[150px] h-12 rounded-xl bg-green-500 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-green-500/25"
            whileTap={{ scale: 0.95 }}
            data-testid="add-btn"
          >
            <Plus size={20} weight="bold" />
            {t('add')}
          </motion.button>
        </div>
        
      </div>
      
      <TransactionModal
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        type={transactionType}
        character={selectedCharacter}
      />
      
      <GoalModal
        isOpen={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        character={selectedCharacter}
        editGoal={editingGoal}
      />
      
      <SettingsSheet
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onDeleteCharacter={handleDeleteCharacter}
        character={selectedCharacter}
      />
    </div>
  );
};

// Main App
const MainApp = () => {
  const { state, dispatch } = useApp();

  const handleOpenWallet = (characterId) => {
    dispatch({ type: 'SELECT_CHARACTER', payload: characterId });
    dispatch({ type: 'SET_VIEW', payload: 'wallet' });
  };
  
  return (
    <>
      <AnimatePresence mode="wait">
        {state.currentView === 'home' ? (
          <motion.div
            key="home"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <HomeScreen onOpenWallet={handleOpenWallet} />
          </motion.div>
        ) : (
          <motion.div
            key="wallet"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <WalletScreen onBack={() => dispatch({ type: 'SET_VIEW', payload: 'home' })} />
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="app-footer-pinned">
        {state.currentView === "home" && (
          <p className="mb-1.5 text-center text-[11px] leading-tight text-[var(--text-muted)] pointer-events-auto">
            Vuoi uno strumento per gestire il combat? Prova{" "}
            <a
              href="https://combatco.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--primary)] underline underline-offset-2 hover:text-[var(--primary-hover)]"
            >
              CoCo
            </a>
            !
          </p>
        )}
        <AdBanner />
      </footer>
      
      <Toaster 
        position="top-center"
        offset={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
        mobileOffset={{ top: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
        toastOptions={{
          style: {
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
          },
        }}
      />
    </>
  );
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
